import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { USER_TYPE_SELLER } from '../../../util/types';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, ExternalLink } from '../../../components';

import productAgreementPdfLink from './product-agreement.pdf';
import css from './ConfirmSignupForm.module.css';

const KEY_CODE_ENTER = 13;

const ConfirmSignupFormComponent = props => (
  <FinalForm
    {...props}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        onOpenTermsOfService,
        authInfo,
        idp,
        userType,
      } = formRenderProps;

      const isSellerSignup = userType === USER_TYPE_SELLER;

      // email
      const emailLabel = intl.formatMessage({
        id: isSellerSignup ? 'ConfirmSignupForm.businessEmailLabel' : 'ConfirmSignupForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // firstName
      const firstNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNameLabel',
      });
      const firstNamePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNamePlaceholder',
      });
      const firstNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.firstNameRequired',
      });
      const firstNameRequired = validators.required(firstNameRequiredMessage);

      // lastName
      const lastNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNameLabel',
      });
      const lastNamePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNamePlaceholder',
      });
      const lastNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.lastNameRequired',
      });
      const lastNameRequired = validators.required(lastNameRequiredMessage);

      // business name
      const businessNameLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.businessNameLabel',
      });
      const businessNamePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.businessNamePlaceholder',
      });
      const businessNameRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.businessNameRequired',
      });
      const businessNameRequired = validators.required(businessNameRequiredMessage);

      // business name
      const websiteLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.websiteLabel',
      });
      const websitePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.websitePlaceholder',
      });
      const websiteLinkInvalidMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.websiteLinkInvalid',
      });
      const websiteLinkInvalid = validators.validURL(websiteLinkInvalidMessage);

      const websiteRequired = validators.required(websiteRequiredMessage);

      const addressLine1Label = intl.formatMessage({
        id: 'ConfirmSignupForm.addressLine1Label',
      });

      const addressLine1Placeholder = intl.formatMessage({
        id: 'ConfirmSignupForm.addressLine1Placeholder',
      });

      const addressLine1RequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.addressLine1RequiredMessage',
      });

      const addressLine1Required = validators.required(addressLine1RequiredMessage);

      const addressLine2Label = intl.formatMessage({
        id: 'ConfirmSignupForm.addressLine2Label',
      });

      const addressLine2Placeholder = intl.formatMessage({
        id: 'ConfirmSignupForm.addressLine2Placeholder',
      });

      const cityLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.cityLabel',
      });

      const cityPlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.cityPlaceholder',
      });

      const cityRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.cityRequiredMessage',
      });

      const cityRequired = validators.required(cityRequiredMessage);

      const stateLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.stateLabel',
      });

      const statePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.statePlaceholder',
      });

      const stateRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.stateRequiredMessage',
      });

      const stateRequired = validators.required(stateRequiredMessage);

      const postCodeLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.postCodeLabel',
      });

      const postCodePlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.postCodePlaceholder',
      });

      const postCodeRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.postCodeRequiredMessage',
      });

      const postCodeRequired = validators.required(postCodeRequiredMessage);

      const countryLabel = intl.formatMessage({
        id: 'ConfirmSignupForm.countryLabel',
      });

      const countryPlaceholder = intl.formatMessage({
        id: 'ConfirmSignupForm.countryPlaceholder',
      });

      const countryRequiredMessage = intl.formatMessage({
        id: 'ConfirmSignupForm.countryRequiredMessage',
      });

      const countryRequired = validators.required(countryRequiredMessage);

      const phoneNumberLabel = intl.formatMessage({
        id: 'SignupForm.phoneNumberLabel',
      });
      const phoneNumberPlaceholder = intl.formatMessage({
        id: 'SignupForm.phoneNumberPlaceholder',
      });

      const phoneNumberRequiredMessage = intl.formatMessage({
        id: 'SignupForm.phoneNumberRequiredMessage',
      });

      const phoneNumberRequired = validators.required(phoneNumberRequiredMessage);

      const minimumOrderLabel = intl.formatMessage({
        id: 'SignupForm.minimumOrderLabel',
      });
      const minimumOrderPlaceholder = intl.formatMessage({
        id: 'SignupForm.minimumOrderPlaceholder',
      });

      const minimumOrderRequiredMessage = intl.formatMessage({
        id: 'SignupForm.minimumOrderRequiredMessage',
      });

      const minimumOrderRequired = validators.required(minimumOrderRequiredMessage);

      const numberOfProductsLabel = intl.formatMessage({
        id: 'SignupForm.numberOfProductsLabel',
      });
      const numberOfProductsPlaceholder = intl.formatMessage({
        id: 'SignupForm.numberOfProductsPlaceholder',
      });

      const numberOfProductsRequiredMessage = intl.formatMessage({
        id: 'SignupForm.numberOfProductsRequiredMessage',
      });

      const numberOfProductsRequired = validators.required(numberOfProductsRequiredMessage);

      const offerSamplesLabel = intl.formatMessage({
        id: 'SignupForm.offerSamplesLabel',
      });
      const offerSamplesPlaceholder = intl.formatMessage({
        id: 'SignupForm.offerSamplesPlaceholder',
      });

      const offerSamplesRequiredMessage = intl.formatMessage({
        id: 'SignupForm.offerSamplesRequiredMessage',
      });

      const offerSamplesRequired = validators.required(offerSamplesRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const handleTermsKeyUp = e => {
        // Allow click action with keyboard like with normal links
        if (e.keyCode === KEY_CODE_ENTER) {
          onOpenTermsOfService();
        }
      };
      const termsLink = (
        <span
          className={css.termsLink}
          onClick={onOpenTermsOfService}
          role="button"
          tabIndex="0"
          onKeyUp={handleTermsKeyUp}
        >
          <FormattedMessage id="ConfirmSignupForm.termsAndConditionsLinkText" />
        </span>
      );
      const productAgreementLink = (
        <ExternalLink className={css.termsLink} href={productAgreementPdfLink}>
          <FormattedMessage id="ConfirmSignupForm.productAgreementText" />
        </ExternalLink>
      );

      // If authInfo is not available we should not show the ConfirmForm
      if (!authInfo) {
        return;
      }

      // Initial values from idp provider
      const { email, firstName, lastName } = authInfo;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div>
            <div className={css.name}>
              <FieldTextInput
                className={css.firstNameRoot}
                type="text"
                id={formId ? `${formId}.firstName` : 'firstName'}
                name="firstName"
                autoComplete="given-name"
                label={firstNameLabel}
                placeholder={firstNamePlaceholder}
                initialValue={firstName}
                validate={firstNameRequired}
              />
              <FieldTextInput
                className={css.lastNameRoot}
                type="text"
                id={formId ? `${formId}.lastName` : 'lastName'}
                name="lastName"
                autoComplete="family-name"
                label={lastNameLabel}
                placeholder={lastNamePlaceholder}
                initialValue={lastName}
                validate={lastNameRequired}
              />
            </div>
            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              initialValue={email}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />
            {isSellerSignup ? (
              <>
                <FieldTextInput
                  className={css.businessName}
                  type="text"
                  id={formId ? `${formId}.businessName` : 'businessName'}
                  name="businessName"
                  autoComplete="given-name"
                  label={businessNameLabel}
                  placeholder={businessNamePlaceholder}
                  initialValue={`${firstName} ${lastName}`}
                  validate={businessNameRequired}
                />
                <FieldTextInput
                  className={css.address}
                  type="text"
                  id={formId ? `${formId}.addressLine1` : 'addressLine1'}
                  name="addressLine1"
                  autoComplete="address-line1"
                  label={addressLine1Label}
                  placeholder={addressLine1Placeholder}
                  validate={addressLine1Required}
                />
                <FieldTextInput
                  className={css.address}
                  type="text"
                  id={formId ? `${formId}.addressLine2` : 'addressLine2'}
                  name="addressLine2"
                  autoComplete="address-line2"
                  label={addressLine2Label}
                  // placeholder={addressLine2Placeholder}
                />
                 <FieldTextInput
                  className={css.address}
                  type="text"
                  id={formId ? `${formId}.city` : 'city'}
                  name="city"
                  autoComplete="address-level2"
                  label={cityLabel}
                  placeholder={cityPlaceholder}
                  validate={cityRequired}
                />
                <div className={css.location}>
                  <FieldTextInput
                    className={css.locationRoot}
                    type="text"
                    id={formId ? `${formId}.postCode` : 'postCode'}
                    name="postCode"
                    autoComplete="postal-code"
                    label={postCodeLabel}
                    placeholder={postCodePlaceholder}
                    validate={postCodeRequired}
                  />
                  <FieldTextInput
                    className={css.locationRoot}
                    type="text"
                    id={formId ? `${formId}.country` : 'country'}
                    name="country"
                    autoComplete="country-name"
                    label={countryLabel}
                    placeholder={countryPlaceholder}
                    validate={countryRequired}
                  />
                </div>
                <FieldTextInput
                  className={css.website}
                  type="website"
                  id={formId ? `${formId}.website` : 'website'}
                  name="website"
                  autoComplete="website"
                  label={websiteLabel}
                  placeholder={websitePlaceholder}
                  validate={validators.composeValidators(websiteRequired, websiteLinkInvalid)}
                />
                <FieldTextInput
                  className={css.phoneNumber}
                  type="phoneNumber"
                  id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                  name="phoneNumber"
                  autoComplete="phoneNumber"
                  label={phoneNumberLabel}
                  placeholder={phoneNumberPlaceholder}
                />
                <FieldTextInput
                  className={css.minimumOrder}
                  type="number"
                  id={formId ? `${formId}.minimumOrder` : 'minimumOrder'}
                  name="minimumOrder"
                  label={minimumOrderLabel}
                  placeholder={minimumOrderPlaceholder}
                  validate={minimumOrderRequired}
                />
                <FieldTextInput
                  className={css.numberOfProducts}
                  type="number"
                  id={formId ? `${formId}.numberOfProducts` : 'numberOfProducts'}
                  name="numberOfProducts"
                  label={numberOfProductsLabel}
                  placeholder={numberOfProductsPlaceholder}
                  validate={numberOfProductsRequired}
                />
                <FieldSelect
                  className={css.offerSamples}
                  id={formId ? `${formId}.offerSamples` : 'offerSamples'}
                  name="offerSamples"
                  label={offerSamplesLabel}
                  placeholder={offerSamplesPlaceholder}
                  validate={offerSamplesRequired}
                >
                  <option value='' disabled>{offerSamplesPlaceholder}</option>
                  <option>Yes</option>
                  <option>No</option>
                </FieldSelect>
              </>
            ) : (
              <>
                <FieldTextInput
                  className={css.businessName}
                  type="buyerBusinessName"
                  id={formId ? `${formId}.buyerBusinessName` : 'buyerBusinessName'}
                  name="buyerBusinessName"
                  label={businessNameLabel}
                  placeholder={businessNamePlaceholder}
                />
                <FieldTextInput
                  className={css.website}
                  type="website"
                  id={formId ? `${formId}.website` : 'website'}
                  name="website"
                  autoComplete="website"
                  label={websiteLabel}
                  placeholder={websitePlaceholder}
                  validate={websiteLinkInvalid}
                />
              </>
            )}
          </div>

          {isSellerSignup ? (
            <div className={css.bottomWrapper}>
              <p className={css.bottomWrapperHighlightedText}>
                <ExternalLink
                  className={css.termsText}
                  href="https://docs.google.com/presentation/d/19GDfHRrrK7paxunoxOcVbsUkemGKq4Nc1JBlnhiFYPc/edit"
                >
                  <FormattedMessage id="ConfirmSignupForm.vendorDeck" />
                </ExternalLink>
              </p>
              <p className={css.bottomWrapperText}>
                <span className={css.termsText}>
                  <FormattedMessage
                    id="ConfirmSignupForm.termsAndConditionsAcceptText"
                    values={{ termsLink, productAgreementLink }}
                  />
                </span>
              </p>
              <PrimaryButton
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp }} />
              </PrimaryButton>
            </div>
          ) : (
            <div className={css.bottomWrapper}>
              <p className={css.bottomWrapperText}>
                <span className={css.termsText}>
                  <FormattedMessage
                    id="ConfirmSignupForm.termsAndConditionsBuyerAcceptText"
                    values={{ termsLink }}
                  />
                </span>
              </p>
              <PrimaryButton
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp }} />
              </PrimaryButton>
            </div>
          )}
        </Form>
      );
    }}
  />
);

ConfirmSignupFormComponent.defaultProps = { inProgress: false };

const { bool, func } = PropTypes;

ConfirmSignupFormComponent.propTypes = {
  inProgress: bool,

  onOpenTermsOfService: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ConfirmSignupForm = compose(injectIntl)(ConfirmSignupFormComponent);
ConfirmSignupForm.displayName = 'ConfirmSignupForm';

export default ConfirmSignupForm;
